import { useCallback, useState } from "react";
import { ComplianceObjectType } from "~/graphql/supergraphEnums";
import { ProfileCreationModal } from "~/pages/Compliance/CaseDetailsDrawer/Profiles/common/AutoProfileCreationCard/ProfileCreationModal";
import { useIOReusableProfiles } from "~/pages/Compliance/flags";
import ProfileCategorySelector from "~/pages/Compliance/profile/ProfileCreator/ProfileCategorySelector";
import ProfileMethodSelector from "~/pages/Compliance/profile/ProfileCreator/ProfileMethodSelector";
import { toDbId } from "~/utils/graphql-helpers";
import { useInvestorWizard } from "../context";
import { EntityProfileForm } from "./EntityProfileForm";
import { IndividualProfileForm } from "./IndividualProfileForm";
import { JointProfileForm } from "./JointProfileForm";
import { ProfileExtractionPreview } from "./ProfileExtractionPreview";
import { ProfileIntro } from "./ProfileIntro";
import type { ProfileDraft } from "../types";
import type { ManualCategory, ProfileSubStep } from "./types";

/**
 * Resumes the sub-step matching a saved draft so the user's data is shown again for
 * editing. A draft always maps to a concrete step, independent of any flag.
 */
const getDraftSubStep = (draft: ProfileDraft): ProfileSubStep => {
  if (draft.documentId && draft.isPrefilled) return "extraction-preview";
  return draft.category;
};

export const ProfileStep = () => {
  const {
    goBack,
    goNext,
    caseInfo,
    profileDraft,
    setExistingProfile,
    setProfileSearchActive,
    selectedProfileId: savedProfileId,
    selectedProfileName: savedProfileName,
  } = useInvestorWizard();
  const ioReusableProfilesEnabled = useIOReusableProfiles();
  // `null` = fresh start; derive the entry step from the flag each render so a late
  // (async) flag value still routes correctly. A concrete value means the user navigated.
  const [subStep, setSubStep] = useState<ProfileSubStep | null>(() =>
    profileDraft ? getDraftSubStep(profileDraft) : null,
  );
  const currentSubStep: ProfileSubStep =
    subStep ?? (ioReusableProfilesEnabled ? "intro" : "account-type");
  const [manualCategory, setManualCategory] = useState<ManualCategory>(() =>
    profileDraft?.category === ComplianceObjectType.Institution
      ? ComplianceObjectType.Institution
      : ComplianceObjectType.Individual,
  );
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [documentId, setDocumentId] = useState<number | undefined>(profileDraft?.documentId);
  // Seeded from the wizard context so the selection survives the round-trip to the
  // Review step, where ProfileStep fully unmounts.
  const [selectedProfileId, setSelectedProfileId] = useState<string | null>(
    () => savedProfileId,
  );
  const [profileSearch, setProfileSearch] = useState(() => savedProfileName ?? "");

  const arenaId = caseInfo?.clientId ? toDbId(caseInfo.clientId) : undefined;

  const goToSubStep = (step: ProfileSubStep) => {
    setShowUploadModal(false);
    setSubStep(step);
  };

  const goToMethod = (selected: ManualCategory) => {
    setManualCategory(selected);
    goToSubStep("method");
  };

  const selectExistingProfile = (
    id: string,
    name: string,
    guid: string,
    isIaAssociated: boolean,
  ) => {
    setExistingProfile(id, name, guid, isIaAssociated);
    goNext();
  };

  // From a create form's duplicate banner: jump to the profile search prefilled with the
  // entered tax id so the user can review and assign the existing profile.
  const viewExistingProfile = (taxId: string) => {
    setProfileSearch(taxId);
    setProfileSearchActive(true);
    setSelectedProfileId(null);
    goToSubStep("intro");
  };

  // useCallback with empty dependency is added here to avoid the modal's useEffect re-subscribing every render.
  const handleExtractionSuccess = useCallback((docId: number) => {
    setDocumentId(docId);
    setShowUploadModal(false);
    setSubStep("extraction-preview");
  }, []);

  switch (currentSubStep) {
    case "intro":
      return (
        <ProfileIntro
          onBack={goBack}
          onCreateNew={() => goToSubStep("account-type")}
          onSelectExisting={selectExistingProfile}
          selectedId={selectedProfileId}
          onSelectedIdChange={setSelectedProfileId}
          searchValue={profileSearch}
          onSearchChange={setProfileSearch}
        />
      );
    case "method":
      return (
        <>
          <ProfileMethodSelector
            backLabel="Account type"
            onBack={() => goToSubStep("account-type")}
            onSelectManual={() => goToSubStep(manualCategory)}
            onSelectExtraction={() => setShowUploadModal(true)}
          />
          {showUploadModal && (
            <ProfileCreationModal
              arenaId={arenaId}
              onClose={() => setShowUploadModal(false)}
              onManualCreate={() => goToSubStep(manualCategory)}
              onExtractionSuccess={handleExtractionSuccess}
            />
          )}
        </>
      );
    case ComplianceObjectType.Individual:
      return (
        <IndividualProfileForm
          onBack={() => goToSubStep("method")}
          onViewExistingProfile={viewExistingProfile}
        />
      );
    case ComplianceObjectType.Institution:
      return (
        <EntityProfileForm
          onBack={() => goToSubStep("method")}
          onViewExistingProfile={viewExistingProfile}
        />
      );
    case ComplianceObjectType.Joint:
      return <JointProfileForm onBack={() => goToSubStep("account-type")} />;
    case "extraction-preview":
      return documentId ? (
        <ProfileExtractionPreview
          documentId={documentId}
          category={manualCategory}
          onBack={() => goToSubStep("method")}
          onViewExistingProfile={viewExistingProfile}
        />
      ) : null;
    case "account-type":
    default:
      return (
        <ProfileCategorySelector
          heading="Compliance profile"
          title="Select an account type"
          backLabel={ioReusableProfilesEnabled ? "Compliance profile" : "Case information"}
          onBack={ioReusableProfilesEnabled ? () => goToSubStep("intro") : goBack}
          onSelectIndividual={() => goToMethod(ComplianceObjectType.Individual)}
          onSelectEntity={() => goToMethod(ComplianceObjectType.Institution)}
          onSelectJoint={() => goToSubStep(ComplianceObjectType.Joint)}
        />
      );
  }
};
